import React from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Sun, Moon, LogOut, User as UserIcon } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { useTheme } from "@/lib/theme";
import AuthDialog from "@/components/AuthDialog";

const NAV = [
  { to: "/", label: "Home" },
  { to: "/pricing", label: "Pricing" },
];

export default function Navbar() {
  const { user, logout } = useAuth();
  const { theme, setTheme } = useTheme();
  const [authOpen, setAuthOpen] = React.useState(false);
  const loc = useLocation();
  const nav = useNavigate();

  return (
    <header className="sticky top-0 z-40 border-b border-border bg-background/85 backdrop-blur-xl">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-8 h-16 flex items-center justify-between">
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-2 group">
          <div className="w-7 h-7 border border-foreground flex items-center justify-center">
            <span className="font-heading text-sm font-medium tracking-tighter">S</span>
          </div>
          <span className="font-heading text-lg tracking-tighter font-medium">splink<span className="text-accent">.</span>pro</span>
        </Link>

        <nav className="hidden md:flex items-center gap-8">
          {NAV.map((n) => (
            <Link
              key={n.to}
              to={n.to}
              data-testid={`nav-${n.label.toLowerCase()}`}
              className={`text-sm font-medium transition-opacity hover:opacity-100 ${loc.pathname === n.to ? "opacity-100" : "opacity-60"}`}
            >
              {n.label}
            </Link>
          ))}
          {user && (
            <>
              <Link to="/dashboard" data-testid="nav-dashboard" className={`text-sm font-medium transition-opacity hover:opacity-100 ${loc.pathname.startsWith("/dashboard") ? "opacity-100" : "opacity-60"}`}>Dashboard</Link>
              <Link to="/billing" data-testid="nav-billing" className={`text-sm font-medium transition-opacity hover:opacity-100 ${loc.pathname.startsWith("/billing") ? "opacity-100" : "opacity-60"}`}>Billing</Link>
              {user.is_admin && (
                <Link to="/admin" data-testid="nav-admin" className={`text-sm font-medium transition-opacity hover:opacity-100 ${loc.pathname.startsWith("/admin") ? "opacity-100" : "opacity-60"}`}>Admin</Link>
              )}
            </>
          )}
        </nav>

        <div className="flex items-center gap-3">
          <button
            data-testid="theme-toggle"
            onClick={() => setTheme(theme === "dark" ? "light" : "dark")}
            className="w-8 h-8 flex items-center justify-center border border-border hover:bg-secondary transition-colors"
            aria-label="Toggle theme"
          >
            {theme === "dark" ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
          </button>

          {user ? (
            <div className="flex items-center gap-2">
              <div className="hidden sm:flex items-center gap-2 px-3 py-1.5 border border-border">
                <UserIcon className="w-3.5 h-3.5" />
                <span className="text-xs font-mono truncate max-w-[140px]" data-testid="nav-user-email">{user.email || user.name}</span>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={async () => { await logout(); nav("/"); }}
                data-testid="nav-logout"
                className="border border-border rounded-none"
              >
                <LogOut className="w-3.5 h-3.5" />
              </Button>
            </div>
          ) : (
            <Button
              onClick={() => setAuthOpen(true)}
              data-testid="nav-signin"
              className="rounded-none bg-foreground text-background hover:bg-foreground/90"
            >
              Sign in
            </Button>
          )}
        </div>
      </div>
      <AuthDialog open={authOpen} onOpenChange={setAuthOpen} />
    </header>
  );
}
